// ── #675, GATE 675 D19-D21, D13 AND #754, Session 79.22b.5.5 -- THE TRANSFERS
// DESK ───────────────────────────────────────────────────────────────────────
//
// What a person sees: on a window 1024px or wider, Dallas's manager has the
// list on the left and the transfer they chose on the right. The chosen card is
// tinted. Changing tab keeps the transfer open when the new tab holds it, and
// otherwise the right side goes back to "Choose a transfer to see it here."
//
// THE RULES ARE HERE, PURE, so one test file can walk every one of them and the
// page only asks.

import { isApprovable, makeProgress, transferRoute } from './branchTransfers';
import { isLoadable, hubsWaitingFor, arrivingFor } from './transferLoads';
import { deriveStage, TAB_LABELS } from './transferStageActions';
import { SHEET_EDGES } from './transferTabs';

// D20 -- the right side before anything is chosen. Nothing opens by itself:
// the right side carries buttons that act, and one opening on its own invites
// acting on a transfer nobody chose (Material's placeholder rule).
export const DESK_PLACEHOLDER = 'Choose a transfer to see it here.';

// D19 -- the list column's width, and where the split starts (Stede's desk
// tier, #417; useTier()'s atLeast('desk')).
export const DESK_LIST_WIDTH = 400;
export const DESK_GAP = 16;

// #755, Session 79.22b.5.5 -- AND ONLY WHEN THE PAGE HAS ROOM. D19 started the
// split at a 1024px WINDOW, assuming "a 905px page". With the left-hand menu
// open the page is 789px, and the chosen transfer got 293px. The gate's own
// research measured the boundary by the layout's own width (Material 840px,
// SAP 960px), so the split now asks the page: the 400px list, the gap, and at
// least 440px for the transfer - 856px inside the page. Narrower, the page is
// one column and the transfer opens as today's sheet, until the menu closes.
// #757, Session 79.22b.5.5c -- the list and the transfer now sit INSIDE the tab's
// sheet, whose border and padding take 26px of the page's width, so the page
// needs those too or the transfer gets 414px: 882px inside the page.
export const DESK_MIN_DETAIL = 440;
export const DESK_MIN_PAGE = DESK_LIST_WIDTH + DESK_GAP + DESK_MIN_DETAIL + SHEET_EDGES;

export function deskSplits({ deskTier = false, pageWidth = null } = {}) {
  if (!deskTier) return false;
  // Not yet measured (the first paint, or a browser with no ResizeObserver):
  // trust the tier, which is right with the menu closed.
  if (pageWidth == null || !Number.isFinite(Number(pageWidth))) return true;
  return Number(pageWidth) >= DESK_MIN_PAGE;
}

// D21 -- the chosen card: tinted green with a green edge, the house pair the
// branch picker already uses for its chosen row (#1D9E75 on #F0FFF4). Applied
// over a card's own style. THE EDGE IS A SHADOW, NOT A BORDER COLOUR: the cards
// set `border` and `borderLeft`, and changing a longhand beside a shorthand on
// a re-render is the warning #224's longhand borders exist to avoid. A truck
// card's left edge keeps its stage colour underneath.
export const CHOSEN_CARD = Object.freeze({
  background: '#F0FFF4',
  boxShadow: '0 0 0 2px #1D9E75',
});

// Whether a card is the one open on the right. A truck card stands for the
// transfer it carries.
export function isChosenCard(chosenId, transferId) {
  return chosenId != null && transferId != null && String(chosenId) === String(transferId);
}

// ── #754 -- WHICH BRANCHES A NARROW WINDOW SHOWS ───────────────────────────
//
// All branches at once is a wide-window view (#370 W8/W9; #520 makes it say
// why when it is refused). The page's default never asked the width, so an
// admin on a phone opened on All branches while the branch choices said All
// branches needs a wider window. Below the workbench width an admin or
// executive now opens on their own branch, and one who chose All branches on a
// wide window drops back to it when the window narrows (Colby's R1 at
// 79.22b.5.5: Stock Home's rule, `isWorkbench && canSeeAllBranches && viewAll`).
//
// `branchView` is the reader's choice: null (their role's default), 'all', or
// a branch id. Returns whether every branch is on screen and, when one is,
// which: never nothing, because an empty branch id would empty every tab and
// read as "there is no work".
export function branchViewFor({ branchView = null, seesEveryBranchByRole = false, isWorkbench = true, myBranch = null } = {}) {
  const wide = !!isWorkbench;
  const effective = branchView ?? (seesEveryBranchByRole && wide ? 'all' : null);
  const seesEveryBranch = effective === 'all' && !!seesEveryBranchByRole && wide;
  const lookingAt = seesEveryBranch ? null : (effective === 'all' || effective == null ? myBranch : effective);
  return { seesEveryBranch, lookingAt };
}

// ── D21 -- DOES A TAB HOLD THIS TRANSFER? ──────────────────────────────────
//
// Asked when the reader changes tab on the desk: the transfer stays open when
// the new tab holds it, as the reader would find it there now (the tab's list
// after the search and the filters), and otherwise the right side returns to
// the placeholder. `lists` is the page's own per-tab lists:
//   transfers: confirm, to_load, awaiting (the hub's "collects this here")
//   To make rows: { kind: 'order', transfer }
//   owed rows: { transfer }
//   trucks and hub pickups: { transfer_id }
// Finished holds every finished transfer, whichever page of it is on screen,
// and the waiting refusals pinned at its top (#752). Which statuses count as
// finished is the page's own FINISHED_STATUSES, handed in, so there is one list.
// GROK'S REVIEW, 79.22b.5.5 -- a refusal still waiting for its decision is often
// a part-refused transfer whose status is not a finished one, and the waiting
// group is read only once Finished is open, so asking the group alone cleared
// the transfer at the very moment the reader went to Finished to decide it.
// `awaitsDecision` is the page's refusalAwaitsDecision(), the group's own rule.
const same = (a, b) => a != null && b != null && String(a) === String(b);

export function tabHoldsTransfer(tabId, transfer, lists = {}, { finishedStatuses = [], awaitsDecision = null } = {}) {
  if (!transfer) return false;
  const id = transfer.id;
  const byId = (rows) => (rows || []).some(t => same(t?.id, id));
  const byTruck = (rows) => (rows || []).some(l => same(l?.transfer_id, id));
  switch (tabId) {
    case 'confirm': return byId(lists.confirm);
    case 'to_make': return (lists.toMake || []).some(r => r?.kind === 'order' && same(r.transfer?.id, id));
    case 'to_load': return byId(lists.toLoad) || byId(lists.awaiting);
    case 'moving': return byTruck(lists.elsewhere) || (lists.owed || []).some(r => same(r?.transfer?.id, id));
    case 'collect': return byTruck(lists.collect) || byTruck(lists.hubPickups);
    case 'count_in': return byTruck(lists.arriving);
    case 'finished': return finishedStatuses.includes(transfer.status) || byId(lists.waiting)
      || (typeof awaitsDecision === 'function' && !!awaitsDecision(transfer));
    default: return false;
  }
}

// ── D13 -- THE DESK'S FILTER BUTTONS ───────────────────────────────────────
//
// "Sort: Due soonest" names the order in force; a filter's button reads "From"
// until one is chosen and then reads the choice in the chip's own words
// ("Coming to Dallas"), carrying its own x.
export function sortButtonLabel(sortLabel) {
  return sortLabel ? `Sort: ${sortLabel}` : 'Sort';
}

// The order the buttons sit in, and which a tab offers (filtersOffered()).
export function deskFilterButtons(offered = {}) {
  return ['from', 'to', 'due', 'when', 'ended'].filter(k => !!offered[k]);
}

export const DESK_FILTER_NAMES = Object.freeze({
  from: 'From',
  to: 'To',
  due: 'Due',
  when: 'Finished',
  ended: 'How it ended',
});

// ── GATE 675 D22, Session 79.22b.5.5b -- AFTER A PRESS ON THE RIGHT SIDE ──────
//
// What a person sees: Dallas's manager approves an ask on the right. The answer
// shows at the top of the right side and the page does not move. The ask has
// left Awaiting confirmation, so the answer adds where it went -- "It's now on To
// make at Houston." -- and a button opens the next ask on the tab. The card they
// acted on keeps its place in the list, showing its new stage, until they choose
// another card or change tab (665 D21's rule, extended to the right side).

// WHICH TAB HOLDS A TRANSFER FOR A PERSON AT A GIVEN BRANCH. One rule for the
// three things that ask it: a link's landing and the search's Look at
// (openOnItsTab in the page), and "It's now on" below. Lifted out of the page so
// a bell and the sentence can never name different tabs (locked rule 14); the
// reasoning for each question stays beside openOnItsTab.
//
// `loads` is the transfer's own trucks (fetchLoads). Only the receiving branch
// collects or counts in, so they are needed only when it is the one asked.
//
// #730, AMENDED at 79.22b.5.5b -- THE RECEIVE QUESTION ASKS THE TRUCKS. It asked
// the transfer's status ("in transit"), so a part-sent transfer whose truck had
// been counted in landed Dallas on To count in with nothing on it to count, and
// one ended "nothing more coming" with its last truck on the road landed on
// Finished while that truck waited on To count in. A truck coming to the branch,
// or standing there uncounted, is what To count in lists -- asked here by the
// sheet's own arrival rule, arrivingFor(), because a transfer's own trucks
// (fetchLoads) do not carry the transfer, and To count in's list rule
// (arrivingAt) reads the destination off it.
export function stageAt(transfer, branchId, { loads = [], finishedStatuses = [] } = {}) {
  if (!transfer) return null;
  const lines = transfer.branch_transfer_items || [];
  const atSending = same(transfer.from_branch_id, branchId);
  const atReceiving = same(transfer.to_branch_id, branchId);
  const ended = finishedStatuses.includes(transfer.status);
  return deriveStage({
    isApprovable: !ended && isApprovable(transfer),
    hasUnmadeLines: !ended && atSending
      && transfer.fulfilment_mode === 'make' && makeProgress(lines).toMake > 0,
    isLoadable: !ended && atSending && isLoadable(transfer, lines),
    waitingAtHub: atReceiving && hubsWaitingFor(loads, transfer).length > 0,
    isReceivable: atReceiving && arrivingFor(loads, transfer, branchId).loads.length > 0,
    isFinished: ended,
  });
}

// WHERE A TRANSFER WENT, once a press has moved it off the tab: the tab where
// the next person who has to act on it will find it, and their branch. Both ends
// are asked by the rule above, and the first job in the order a transfer travels
// wins -- so a transfer Dallas has just counted in while Houston still makes the
// rest is "on To make at Houston", not Dallas's empty To count in. With no job
// at either end: Finished once it has ended; a refusal still waiting for the
// asking branch is on that branch's Finished (#752's group); otherwise it is
// owed to the asking branch, on Out & owed.
const JOB_TABS = ['confirm', 'to_make', 'to_load', 'collect', 'count_in'];

export function whereItNowIs(transfer, { loads = [], finishedStatuses = [], awaitsDecision = null } = {}) {
  if (!transfer) return null;
  const opts = { loads, finishedStatuses };
  // The supplying end first: Awaiting confirmation is its question
  // (canConfirmSupply), and both ends answer "confirm" alike.
  const ends = [transfer.from_branch_id, transfer.to_branch_id]
    .map(branchId => ({ branchId, tab: stageAt(transfer, branchId, opts) }))
    .filter(e => JOB_TABS.includes(e.tab));
  if (ends.length) {
    return ends.reduce((a, b) => (JOB_TABS.indexOf(b.tab) < JOB_TABS.indexOf(a.tab) ? b : a));
  }
  if (finishedStatuses.includes(transfer.status)) return { tab: 'finished', branchId: null };
  if (typeof awaitsDecision === 'function' && awaitsDecision(transfer)) {
    return { tab: 'finished', branchId: transfer.to_branch_id };
  }
  return { tab: 'moving', branchId: transfer.to_branch_id };
}

// "It's now on To make at Houston." The branch is named when it is not the one
// the reader works at; Finished is everybody's, so it never carries one.
export function nowOnSentence(where, { branchNames = {}, myBranch = null } = {}) {
  const label = where ? TAB_LABELS[where.tab] : null;
  if (!label) return null;
  const elsewhere = where.branchId != null && !same(where.branchId, myBranch);
  const at = elsewhere ? (branchNames[String(where.branchId)] || null) : null;
  return `It's now on ${label}${at ? ` at ${at}` : ''}.`;
}

// NEXT ON {TAB}: the next card still waiting on the tab, in the list's own order
// with its search and filters -- the cards as drawn. `cards` is that order, one
// entry per card: { transferId, transfer, pinned }. From the card the press was
// made on, down the list and then from the top (Colby's R1 at 79.22b.5.5b),
// skipping every card of the transfer just acted on (a truck tab can hold
// several) and any card kept in place. Null when nothing else is waiting, and
// the button is then not drawn (F3).
export function nextCardAfter(cards = [], actedId = null) {
  const list = Array.isArray(cards) ? cards : [];
  const n = list.length;
  if (!n) return null;
  const at = list.findIndex(c => c?.pinned && same(c.transferId, actedId));
  for (let k = 1; k <= n; k += 1) {
    const c = list[(at + k) % n];
    if (!c || c.pinned || same(c.transferId, actedId)) continue;
    return c;
  }
  return null;
}

// The button's words: "Next on Awaiting confirmation: San Antonio → Dallas". A
// transfer is numbered when it is approved, so an ask still waiting has no
// number yet and is named by its route, the way its card prints it (#675's
// finding, Colby's R1); every numbered card is named by its number, as drawn.
export function nextButtonLabel(tabId, transfer, branchNames = {}) {
  const name = transfer?.transfer_number || transferRoute(transfer, branchNames);
  const tabName = TAB_LABELS[tabId] || tabId;
  return name ? `Next on ${tabName}: ${name}` : `Next on ${tabName}`;
}

// The cards on a tab, in the order the page draws them, for Next. The page hands
// in its own lists (search, filters and any card kept in place applied) and how
// to find a truck's transfer. Reserve rows on To make are stock running low,
// not transfers, so they are never "next"; on Finished only the refusals
// waiting for a decision are work (#752), never its history.
export function cardsOnTab(tabId, lists = {}, transferOf = () => null) {
  const ofTransfer = t => ({ transferId: t?.id ?? null, transfer: t || null, pinned: !!t?.__pinned });
  const ofTruck = (l, pinned = !!l?.__pinned) => {
    const id = l?.transfer_id ?? l?.branch_transfers?.id ?? null;
    return { transferId: id, transfer: transferOf(id, l), pinned };
  };
  switch (tabId) {
    case 'confirm':
      return (lists.confirm || []).map(ofTransfer);
    case 'to_make':
      return (lists.toMake || []).filter(r => r?.kind === 'order')
        .map(r => ({ transferId: r.transfer?.id ?? null, transfer: r.transfer || null, pinned: !!r.pinned }));
    case 'to_load':
      return [...(lists.toLoad || []), ...(lists.awaiting || [])].map(ofTransfer);
    case 'moving':
      return [
        ...(lists.owed || []).map(r => ({ transferId: r?.transfer?.id ?? null, transfer: r?.transfer || null, pinned: !!r?.__pinned })),
        ...(lists.elsewhere || []).map(l => ofTruck(l)),
      ];
    case 'collect':
      return [...(lists.hubPickups || []), ...(lists.collect || [])].map(l => ofTruck(l));
    case 'count_in':
      return (lists.arriving || []).map(l => ofTruck(l));
    case 'finished':
      return (lists.waiting || []).map(ofTransfer);
    default:
      return [];
  }
}
